<template>
  <fieldset
    :disabled="disabled"
    class="text-center">
    <div :class="[ 'text-center', inline? 'flex flex-col md:flex-row md:justify-between md:items-center gap-3' : '']">
      <button
        type="button"
        ref="fileInputButton"
        :data-testid="buttonDataTestId"
        :aria-controls="id"
        @click="clickFileInput"
        @dragenter.stop.prevent="onDragEnterHandler"
        @dragover.stop.prevent
        @drop.stop.prevent="onDropHandler"
        @dragleave.stop.prevent="onDragLeaveHandler"
        :class="buttonCssClasses"
      >
        <slot name="buttonContents">
          <UploadIcon
            title="File upload"
            class="me-[.5em] inline" />
          {{ buttonTextContent }}
        </slot>
      </button>

      <input
        type="file"
        ref="fileInput"
        :id="id"
        :name="name"
        :required="required"
        :accept="accept"
        :multiple="multiple"
        :aria-label="labelTextContent"
        :aria-invalid="isInvalid"
        :aria-errormessage="errorMessageIds"
        class="peer sr-only"
        @input="onInputHandler"
        @click="onClickHandler"
      />

      <slot name="optionCue">
        <span :class="spanCssClasses">or</span>
      </slot>
      <label
        :class="labelCssClasses"
        :for="id"
        :data-testid="labelDataTestId">
        {{ labelTextContent }}
      </label>
    </div>

    <slot
      name="srAnnouncements"
      :defaultMessages="srMessage">
      <div
        aria-live="polite"
        class="sr-only">
        {{ srMessage }}
      </div>
    </slot>

    <ul
      v-if="hasSelectedFiles && showSelectedFiles"
      aria-label="Selected files"
      :class="filesListCssClasses"
    >
      <template
        v-for="({ name, size, type, lastModified }, index) in selectedFiles"
        :key="index"
      >
        <slot
          name="selectedFileListItem"
          :fileName="name"
          :fileSize="size"
          :fileType="type"
          :fileLastModified="lastModified"
          :fileIndex="index"
          :removeFile="removeFile"
          :formatFileSize="formatFileSize"
          :cssClasses="filesListItemCssClasses"
        >
          <li :class="filesListItemCssClasses">
            <p>
              <strong>{{ name }}</strong>
              <span>
                (<span class="sr-only">File size:</span
                >{{ formatFileSize(size).size
                }}<abbr
                  class="no-underline"
                  :title="formatFileSize(size).expanded"
                >{{ formatFileSize(size).abbr }}</abbr
                >)</span
              >
            </p>
            <ButtonComponent
              @custom-click="removeFile(index, name)"
              label="Remove"
              :aria-label="`Remove file ${name}`"
              variant="tertiary"
              :disabled="disabled"
            >
              <template #icon>
                <CrossIcon
                  :isDecorative="true"
                  :title="null" />
              </template>
            </ButtonComponent>
          </li>
        </slot>
      </template>
    </ul>
  </fieldset>
</template>

<script>
import UploadIcon from '../ui-components/icons/UploadIcon.vue'
import CrossIcon from '../ui-components/icons/CrossIcon.vue'
import ButtonComponent from '../ui-components/ButtonComponent.vue'

/** Form inputs are input fields for users to enter text or numbers that are no longer than a single line. */
export default {
  name: 'FormInputFile',
  props: {
    id: {
      type: String,
      required: true,
    },
    /** For drag-and-drop. Communicates the action for input */
    buttonText: {
      type: String,
    },
    buttonDataTestId: {
      type: String,
    },
    /** Communicates the action for input */
    labelText: {
      type: String,
    },
    labelDataTestId: {
      type: String,
    },
    /** Optional. */
    name: {
      type: String,
      default: null,
    },
    /** Provide a hint to browsers as to the correct file types. Does not validate. */
    accept: {
      type: Array,
      default: null,
    },
    capture: {
      type: String,
      default: null,
      validator: value => ['user', 'environment'].includes(value),
    },
    /** Allow multiple files to be selected. */
    multiple: {
      type: Boolean,
      default: false,
    },
    /** For accessibility (and validation if HTML5 validation api were to be used)  */
    required: {
      type: Boolean,
      default: false,
    },
    /** For accessibility, to communicate to screen readers that the input is currently invalid. */
    isInvalid: {
      type: Boolean,
      default: false,
    },
    /** For accessibility, to communicate to screen readers that the input is currently invalid. */
    ariaErrorMessageId: {
      type: [String, Array],
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    customButtonCssClasses: {
      type: Array,
      default: null,
    },
    customLabelCssClasses: {
      type: Array,
      default: null,
    },
    customSpanCssClasses: {
      type: Array,
      default: null,
    },
    /** allows customisation of responsive classes */
    customButtonShowCssClass: {
      type: Array,
      default: () => ['hidden', 'md:block'],
    },
    /** allows customisation of responsive classes */
    customSpanShowCssClass: {
      type: Array,
      default: () => ['hidden', 'md:inline'],
    },
    customFilesListCssClass: {
      type: Array,
      default: null,
    },
    customFilesListItemCssClass: {
      type: Array,
      default: null,
    },
    inline: {
      type: Boolean,
      default: false,
    },
    showSelectedFiles: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedFiles: [], // type File[]
      srMessage: '',
      draggingToDrop: false,
    }
  },
  computed: {
    buttonCssClasses() {
      const classes = [
        'hidden',
        'w-full',
        'border-2',
        'border-dashed',
        'border-royal-blue-100',
        'rounded-md',
        'bg-white',
        'font-bold',
        'motion-safe:transition-colors',
        'focus-visible:outline-royal-blue-100',
        'focus-visible:outline',
        'focus-visible:outline-2',
        'focus-visible:outline-offset-2',
        ...this.customButtonShowCssClass,
      ]
      this.inline ? classes.push('py-[16px]', 'basis-[75%]') : classes.push('py-[4em]', 'mb-[2em]')
      if (this.draggingToDrop) {
        classes.push('bg-royal-blue-20', 'cursor-grabbing!')
      }
      this.disabled
        ? classes.push('opacity-20', 'cursor-not-allowed', 'hover:bg')
        : classes.push('cursor-pointer', 'hover:bg-royal-blue-20')
      return this.chooseClassList(this.customButtonCssClasses, classes)
    },
    spanCssClasses() {
      return this.chooseClassList(this.customSpanCssClasses, [
        'font-bold',
        'me-2',
        ...this.customSpanShowCssClass,
      ])
    },
    labelCssClasses() {
      const classes = [
        'block',
        'py-2.5',
        'px-6',
        'bg-royal-blue-100',
        'border-royal-blue-100',
        'border-2',
        'text-white',
        'font-bold',
        'rounded-sm',
        'relative',
        'peer-focus:bg-science-blue-100',
        'peer-focus:border-science-blue-100',
        'md:inline-block',
        'whitespace-nowrap',
      ]
      this.disabled
        ? classes.push('opacity-20', 'cursor-not-allowed')
        : classes.push(
            'cursor-pointer',
            'hover:bg-royal-blue-160',
            'hover:border-royal-blue-160',
          )
      return this.chooseClassList(this.customLabelCssClasses, classes)
    },
    filesListCssClasses() {
      const classes = ['my-[2em]', 'pt-[.5em]', 'border-t', 'text-start']
      return this.chooseClassList(this.customFilesListCssClass, classes)
    },
    filesListItemCssClasses() {
      return this.chooseClassList(this.customFilesListItemCssClass, [
        'flex items-center',
        'justify-between',
        'py-2',
      ])
    },
    hasSelectedFiles() {
      return !!this.selectedFiles.length
    },
    srFileSelectionStatusMessage() {
      const fileCount = this.selectedFiles.length
      const fileWording = fileCount < 1 || fileCount > 1 ? 'files' : 'file'
      return `${fileCount} ${fileWording} currently selected.`
    },
    errorMessageIds() {
      const prop = this.ariaErrorMessageId
      let id
      if (Array.isArray(prop)) {
        id = prop.join(' ')
      }
      else {
        id = prop
      }
      return this.isInvalid ? id : null // ensures attribute is set only when attribute aria-invalid=true
    },
    buttonTextContent() {
      if (this.buttonText) return this.buttonText
      return this.multiple
        ? 'Drag and Drop files here'
        : 'Drag and Drop file here'
    },
    labelTextContent() {
      if (this.labelText) return this.labelText
      return this.multiple ? 'Select files' : 'Select file'
    },
  },
  methods: {
    clickFileInput() {
      this.$refs.fileInput.click()
    },
    onClickHandler(e) {
      // manage focus, to avoid disorientation
      if (e.explicitOriginalTarget?.tagName?.toLowerCase() === 'button') {
        this.$nextTick(() => this.$refs.fileInputButton.focus())
      }
    },
    onInputHandler(e) {
      this.emitCustomInputEvent(e.target.files)
      this.addFiles(e.target.files) // input event exposes (only) newly selected
    },
    onDropHandler(e) {
      this.draggingToDrop = false
      this.emitCustomInputEvent(e.dataTransfer.files)
      this.addFiles(e.dataTransfer.files)
    },
    onDragEnterHandler() {
      this.draggingToDrop = true
    },
    onDragLeaveHandler() {
      this.draggingToDrop = false
    },
    addFiles(files) {
      const filesArray = Array.from(files)
      if (this.multiple) {
        this.selectedFiles.push(...filesArray) // enables user to add files across multiple drop or input attempts
      }
      else {
        this.selectedFiles = [filesArray[0]] // honour absent multiple attribute promise
      }
      this.updateFileInput()
    },
    removeFile(index, name) {
      this.selectedFiles.splice(index, 1)
      this.updateFileInput()
      this.$nextTick(() => this.$refs.fileInput.focus()) // focus managed after disappearing remove button, to avoid user disorientation
      this.updateSrMessage(
        `File '${name}' removed. ${this.srFileSelectionStatusMessage}`,
      )
    },
    updateFileInput() {
      const elFileInput = this.$refs.fileInput
      const dataTransfer = new DataTransfer()
      this.selectedFiles.forEach(file => dataTransfer.items.add(file))
      elFileInput.files = dataTransfer.files // set native element FileList
      this.emitCustomChangeEvent()
      this.updateSrMessage(this.srFileSelectionStatusMessage)
      this.$refs.fileInput.value = ''
    },
    emitCustomInputEvent(files) {
      this.$emit('custom-input', Array.from(files)) // emit (only) new selected files, type File[]
    },
    emitCustomChangeEvent() {
      this.$emit('custom-change', this.selectedFiles) // emit custom change event to notify parent, current selected files, type File[]
    },
    resetSelectedFiles() {
      // allows, for example, parent to clear selectedFiles/input.files
      this.selectedFiles = []
      this.$refs.fileInput.value = ''
      this.emitCustomChangeEvent()
      this.updateSrMessage(this.srFileSelectionStatusMessage)
    },
    formatFileSize(bytes) {
      class Formatted {
        constructor(size, abbr, expanded) {
          this.size = size
          this.abbr = abbr
          this.expanded = expanded
        }
      }
      const sizes = [
        { abbr: 'Bytes', expanded: 'Bytes' },
        { abbr: 'KB', expanded: 'Kilobytes' },
        { abbr: 'MB', expanded: 'Megabytes' },
        { abbr: 'GB', expanded: 'Gigabytes' },
      ]

      if (bytes === 0) {
        return new Formatted(0, sizes[0].abbr, sizes[0].expanded)
      }

      const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)), 10)
      const size = bytes / Math.pow(1024, i)
      return new Formatted(size.toFixed(2), sizes[i].abbr, sizes[i].expanded)
    },
    updateSrMessage(msg) {
      this.srMessage = ''
      this.$nextTick(() => (this.srMessage = msg))
    },
    chooseClassList(custom, standard) {
      if (Array.isArray(custom)) {
        return custom
      }
      else {
        return standard
      }
    },
  },
  components: {
    UploadIcon,
    CrossIcon,
    ButtonComponent,
  },
  emits: ['custom-input', 'custom-change'],
}
</script>
